import { useState } from 'react';
import { CenteredPage } from '../../components/layout/CenteredPage';
import { Button } from '../../components/actions/Button';
import { nuclearReset } from '../../services/appResetService';
import { isAndroid, isIOS, isStandalonePWA } from '../../services/platformService';
import { logError } from '../../services/loggerService';

/**
 * Reached when the /ping lifeline finds this build below the BE's minimum
 * supported version AND an automatic reset has already been tried this
 * session. "Apply update" retries the reset; the reinstall steps are the
 * last resort. Ported from core with the admin origin in the steps.
 */
function reinstallSteps(origin: string): string[] {
  const standalone = isStandalonePWA();
  if (isIOS() && standalone) {
    return [
      'Long-press the Mission Control icon on your Home Screen and tap Remove App.',
      `Open Safari and go to ${origin}.`,
      'Tap Share, then Add to Home Screen.',
    ];
  }
  if (isAndroid() && standalone) {
    return [
      'Long-press the Mission Control icon and tap Uninstall.',
      `Open Chrome and go to ${origin}.`,
      'Open the ⋮ menu and choose Install app.',
    ];
  }
  if (standalone) {
    return [
      'Open the ⋮ menu in the Mission Control window and choose Uninstall.',
      `Open ${origin} in your browser.`,
      'Click the install icon in the address bar.',
    ];
  }
  return ['Hard-reload the page: Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows or Linux.'];
}

export function UpdateRequiredPage() {
  const [applying, setApplying] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  const apply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await nuclearReset();
    } catch (err) {
      logError(err instanceof Error ? err : new Error(String(err)), 'UpdateRequiredPage: nuclearReset threw');
      window.location.reload();
    }
  };

  return (
    <CenteredPage
      title="Update required"
      actions={
        <>
          <Button fullWidth onClick={apply} loading={applying}>
            {applying ? 'Updating…' : 'Apply update'}
          </Button>
          <button
            type="button"
            onClick={() => setShowSteps((v) => !v)}
            className="border-0 bg-transparent text-sm text-muted underline"
          >
            {showSteps ? 'Hide instructions' : 'Still having trouble?'}
          </button>
          {showSteps && (
            <ol className="m-0 list-inside list-decimal space-y-2 pl-0 text-left text-sm text-muted">
              {reinstallSteps(window.location.origin).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </>
      }
    >
      This version of Mission Control is no longer supported. Apply the update to continue.
    </CenteredPage>
  );
}
